import React, { useEffect, useState } from 'react';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import GamepadOutlinedIcon from '@mui/icons-material/GamepadOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ScienceIcon from '@mui/icons-material/Science';
import SettingsIcon from '@mui/icons-material/Settings';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Link, useLocation, useNavigate } from "react-router-dom";
import EMLogo from "../../Configs/Assets/EM Logo.svg";
import { DRAWER_FIXED_MIN_WIDTH, useAppContext } from "../../Helpers/Contexts/AppContext";
import { useAuth } from "../../Helpers/Contexts/AuthContext";
import { isSuperAdmin } from "../../Helpers/permissions";
import { DemoChip, DrawerEmptyStores, DrawerLoading, DrawerSection, DrawerSubItem } from "./DrawerNavItems";

export default function LeftDrawer({
    activeDashboardTab, setActiveDashboardTab,
    selectedLocationFilter = 'todas-lojas',
    setSelectedLocationFilter,
}) {
    const [appContext, setAppContext] = useAppContext();
    const { lojas, loadingLojas, isDemo, user, can, permsReady } = useAuth();
    const [lojasOpen, setLojasOpen] = useState(false);
    const [maquinasOpen, setMaquinasOpen] = useState(false);
    const [pecasOpen, setPecasOpen] = useState(false);
    const [chamadosOpen, setChamadosOpen] = useState(false);
    const [financeiroOpen, setFinanceiroOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const isDashboard = location.pathname === '/home';
    // Celular/tablet: o menu vira gaveta por cima do conteúdo e fecha ao escolher uma tela
    const isMobile = useMediaQuery(`(max-width:${DRAWER_FIXED_MIN_WIDTH - 0.05}px)`);
    const closeDrawer = () => setAppContext((prev) => ({ ...prev, drawerOpened: false }));

    // Mantém aberta a seção do menu lateral correspondente à aba em que o usuário está
    useEffect(() => {
        if (!isDashboard || !activeDashboardTab) return;
        if (activeDashboardTab.startsWith('pecas')) {
            setPecasOpen(true);
        } else if (activeDashboardTab.startsWith('chamados')) {
            setChamadosOpen(true);
        } else if (activeDashboardTab.startsWith('financeiro')) {
            setFinanceiroOpen(true);
        } else if (activeDashboardTab === 'maquinas') {
            setMaquinasOpen(true);
        } else if (lojas && lojas.some((l) => l.id === activeDashboardTab)) {
            setLojasOpen(true);
        }
    }, [isDashboard, activeDashboardTab, lojas]);

    // Modo demo: menu sempre aberto e com todas as seções expandidas, para o visitante ver tudo o que existe
    useEffect(() => {
        if (isDemo && !isMobile && !appContext.drawerOpened) setAppContext((prev) => ({ ...prev, drawerOpened: true }));
    }, [isDemo, isMobile, appContext.drawerOpened, setAppContext]);

    // Permissão restrita de Admin
    const isAdmin = isSuperAdmin(user) || user?.app_metadata?.role === 'admin';

    // Troca a aba do dashboard. `filter` undefined mantém o filtro de loja atual.
    const goToTab = (tab, filter) => {
        if (filter !== undefined && setSelectedLocationFilter) setSelectedLocationFilter(filter);
        if (setActiveDashboardTab) setActiveDashboardTab(tab);
        if (location.pathname !== '/home') navigate('/home');
    };
    // Escolha final de tela (não só abrir uma seção): no celular fecha a gaveta
    const pick = (tab, filter) => {
        goToTab(tab, filter);
        if (isMobile) closeDrawer();
    };

    // Painel e Máquinas são por loja: só listam as lojas em que a tela está liberada
    const lojasPainel = lojas.filter((l) => can('lojas', 'view', l.id));
    const lojasMaquinas = lojas.filter((l) => can('maquinas', 'view', l.id));

    const isTab = (...tabs) => isDashboard && tabs.includes(activeDashboardTab);
    const tabStartsWith = (prefix) => isDashboard && !!activeDashboardTab?.startsWith(prefix);

    return (
        <Drawer
            variant={isMobile ? "temporary" : "persistent"}
            anchor="left"
            open={appContext.drawerOpened}
            onClose={closeDrawer}
            ModalProps={{ keepMounted: true }}
            sx={{
                width: appContext.drawerOpened && !isMobile ? 280 : 0,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: 280,
                    maxWidth: '85vw',
                    boxSizing: 'border-box',
                    top: 0,
                    height: '100%',
                    borderRight: 'none',
                    backgroundColor: isMobile ? 'background.default' : 'transparent', // No desktop herda o fundo de fora; no celular fica por cima do conteúdo
                    zIndex: 4,
                    transition: 'width 0.3s ease',
                    overflowX: 'hidden',
                },
            }}
        >
            <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }} role="presentation">
                {/* ── Top Logo Area (Shadcn Team Switcher style) ────────────── */}
                <Box sx={{ px: 2, py: 2, pt: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        component="img"
                        src={EMLogo}
                        alt="Entertainment Maintenance Logo"
                        sx={{
                            height: 28,
                            width: 'auto',
                            objectFit: 'contain',
                            flexShrink: 0,
                            filter: (theme) => theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : 'none',
                        }}
                    />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                        Entertainment Maintenance
                    </Typography>
                </Box>

                {/* ── Dashboard section ──────────────────────────── */}
                <List
                    disablePadding
                    sx={{
                        px: 1.5,
                        // Só esta parte rola (logo e menu de baixo ficam fixos), sem barra de rolagem visível
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                    }}
                >
                    {/* Até as permissões chegarem o menu fica vazio: nenhuma aba bloqueada chega a aparecer */}
                    {user && !permsReady && <DrawerLoading />}

                    {/* Expandível "Minhas Lojas" — ao expandir, abre a primeira loja da lista */}
                    {can('lojas', 'view') && (<>
                    <DrawerSection
                        first
                        icon={StorefrontIcon}
                        label="Minhas Lojas"
                        open={lojasOpen || isDemo}
                        onToggle={() => setLojasOpen(!lojasOpen)}
                        onSelect={() => {
                            const abrir = !lojasOpen;
                            setLojasOpen(abrir);
                            if (abrir && lojasPainel.length > 0) goToTab(lojasPainel[0].id);
                        }}
                        selected={false}
                        active={isDashboard && lojas.some(l => l.id === activeDashboardTab)}
                        bold={false}
                    />

                    {/* Sub-lista de lojas */}
                    <Collapse in={lojasOpen || isDemo} timeout="auto" unmountOnExit>
                        {loadingLojas && <DrawerLoading />}
                        {!loadingLojas && lojasPainel.map((loja) => (
                            <DrawerSubItem
                                key={loja.id}
                                label={loja.nome}
                                selected={isTab(loja.id)}
                                onClick={() => pick(loja.id)}
                            >
                                {loja.is_demo && <DemoChip />}
                            </DrawerSubItem>
                        ))}
                        {!loadingLojas && lojas.length === 0 && <DrawerEmptyStores />}
                    </Collapse>
                    </>)}

                    {/* Aba "Minhas Máquinas" Expandível — ao expandir, abre a primeira loja da lista */}
                    {can('maquinas', 'view') && (<>
                    <DrawerSection
                        icon={GamepadOutlinedIcon}
                        label="Minhas Máquinas"
                        open={maquinasOpen || isDemo}
                        onToggle={() => setMaquinasOpen(!maquinasOpen)}
                        onSelect={() => {
                            const abrir = !maquinasOpen;
                            setMaquinasOpen(abrir);
                            if (abrir && lojasMaquinas.length > 0) goToTab('maquinas', lojasMaquinas[0].nome);
                        }}
                        selected={false}
                        active={isTab('maquinas')}
                        bold={false}
                    />

                    {/* Sub-lista de Localizações para Minhas Máquinas */}
                    <Collapse in={maquinasOpen || isDemo} timeout="auto" unmountOnExit>
                        {loadingLojas && <DrawerLoading />}
                        {!loadingLojas && lojasMaquinas.map((loja) => {
                            const storeName = loja.nome;
                            const isStoreActive = isTab('maquinas') && (selectedLocationFilter === storeName || selectedLocationFilter?.toLowerCase() === storeName.toLowerCase());
                            return (
                                <DrawerSubItem
                                    key={`maq-store-${loja.id}`}
                                    label={storeName}
                                    selected={isStoreActive}
                                    onClick={() => pick('maquinas', storeName)}
                                >
                                    {loja.is_demo && <DemoChip />}
                                </DrawerSubItem>
                            );
                        })}
                        {!loadingLojas && lojas.length === 0 && <DrawerEmptyStores />}
                    </Collapse>
                    </>)}

                    {/* Aba "Peças & Componentes" Expandível */}
                    {can('pecas-estoque', 'view') && (<>
                    <DrawerSection
                        icon={BuildOutlinedIcon}
                        label="Peças & Componentes"
                        open={pecasOpen || isDemo}
                        onToggle={() => setPecasOpen(!pecasOpen)}
                        onSelect={() => {
                            const abrir = !pecasOpen;
                            setPecasOpen(abrir);
                            if (abrir) goToTab('pecas-estoque', 'todas-lojas');
                        }}
                        selected={false}
                        active={tabStartsWith('pecas')}
                        bold={false}
                    />

                    {/* Sub-lista de Categorias para Peças & Componentes */}
                    <Collapse in={pecasOpen || isDemo} timeout="auto" unmountOnExit>
                        {can('pecas-estoque', 'view') && <DrawerSubItem
                            label="Estoque Geral"
                            selected={isTab('pecas-estoque', 'pecas')}
                            onClick={() => pick('pecas-estoque', 'todas-lojas')}
                        />}
                        {can('pecas-movimentacoes', 'view') && <DrawerSubItem
                            label="Movimentações"
                            selected={isTab('pecas-movimentacoes')}
                            onClick={() => pick('pecas-movimentacoes', 'todas-lojas')}
                        />}
                    </Collapse>
                    </>)}

                    {/* Aba "Central de Chamados" Expandível */}
                    {can('chamados-abertos', 'view') && (<>
                    <DrawerSection
                        icon={ConfirmationNumberOutlinedIcon}
                        label="Central de Chamados"
                        open={chamadosOpen || isDemo}
                        onToggle={() => setChamadosOpen(!chamadosOpen)}
                        onSelect={() => {
                            const abrir = !chamadosOpen;
                            setChamadosOpen(abrir);
                            if (abrir) goToTab('chamados-abertos', 'todas-lojas');
                        }}
                        selected={isTab('chamados')}
                        active={tabStartsWith('chamados')}
                        bold={isTab('chamados')}
                    />

                    {/* Sub-lista de Categorias para Central de Chamados */}
                    <Collapse in={chamadosOpen || isDemo} timeout="auto" unmountOnExit>
                        {can('chamados-abertos', 'view') && <DrawerSubItem
                            label="Chamados"
                            selected={isTab('chamados-abertos')}
                            onClick={() => pick('chamados-abertos', 'todas-lojas')}
                        />}
                        {can('chamados-historico', 'view') && <DrawerSubItem
                            label="Histórico"
                            selected={isTab('chamados-historico')}
                            onClick={() => pick('chamados-historico', 'todas-lojas')}
                        />}
                    </Collapse>
                    </>)}

                    {/* Aba "Financeiro" Expandível */}
                    {can('financeiro-faturamento', 'view') && (<>
                    <DrawerSection
                        icon={AttachMoneyOutlinedIcon}
                        label="Financeiro"
                        open={financeiroOpen || isDemo}
                        onToggle={() => setFinanceiroOpen(!financeiroOpen)}
                        onSelect={() => {
                            const abrir = !financeiroOpen;
                            setFinanceiroOpen(abrir);
                            if (abrir) goToTab('financeiro-faturamento', 'todas-lojas');
                        }}
                        selected={isTab('financeiro')}
                        active={tabStartsWith('financeiro')}
                        bold={tabStartsWith('financeiro')}
                    />

                    {/* Sub-lista de Categorias para Financeiro */}
                    <Collapse in={financeiroOpen || isDemo} timeout="auto" unmountOnExit>
                        {can('financeiro-faturamento', 'view') && <DrawerSubItem
                            label="Faturamento das Máquinas"
                            selected={isTab('financeiro-faturamento', 'financeiro')}
                            onClick={() => pick('financeiro-faturamento', 'todas-lojas')}
                        />}
                        {can('financeiro-custos', 'view') && <DrawerSubItem
                            label="Custos & Despesas"
                            selected={isTab('financeiro-custos')}
                            onClick={() => pick('financeiro-custos', 'todas-lojas')}
                        />}
                    </Collapse>
                    </>)}

                    {/* Aba "Usuários": colegas das mesmas lojas e o que cada um fez */}
                    {can('usuarios', 'view') && <DrawerSection
                        icon={PeopleOutlineIcon}
                        label="Usuários"
                        onSelect={() => pick('usuarios', 'todas-lojas')}
                        selected={isTab('usuarios')}
                        active={isTab('usuarios')}
                        bold={isTab('usuarios')}
                    />}

                    {/* Badge de modo demo abaixo da lista */}
                    {isDemo && !loadingLojas && (
                        <Box sx={{ px: 2, pb: 1, pt: 0.5 }}>
                            <Chip
                                label="Modo Demonstração ativo"
                                size="small"
                                icon={<ScienceIcon sx={{ fontSize: '14px !important' }} />}
                                sx={{
                                    width: '100%',
                                    fontSize: 11,
                                    fontWeight: 600,
                                    bgcolor: 'warning.light',
                                    color: 'warning.dark',
                                    borderRadius: 1.5,
                                    '& .MuiChip-label': { px: 1 },
                                }}
                            />
                        </Box>
                    )}
                </List>

                <Divider sx={{ my: 1 }} />

                {/* ── Secondary Nav (Admin Settings, WhatsApp Help) ────────── */}
                <List sx={{ mt: 'auto', px: 1.5, pb: 1 }} disablePadding>
                    {/* Aba "Logs": só o dev vê o resultado das rotinas do SPIX */}
                    {isSuperAdmin(user) && <DrawerSection
                        icon={ReceiptLongOutlinedIcon}
                        label="Logs"
                        onSelect={() => pick('logs')}
                        selected={isTab('logs')}
                        active={isTab('logs')}
                        bold={isTab('logs')}
                    />}
                    {isAdmin && (
                        <ListItem
                            component={Link}
                            to="/panel/settings"
                            onClick={() => isMobile && closeDrawer()}
                            sx={{
                                borderRadius: 1.5,
                                py: 0.7,
                                px: 1.5,
                                mb: 0.2,
                                cursor: 'pointer',
                                color: 'text.secondary',
                                '&:hover': { bgcolor: 'action.hover', color: 'text.primary' }
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5, color: 'inherit' }}>
                                <SettingsIcon sx={{ fontSize: 18 }} />
                            </ListItemIcon>
                            <ListItemText primary="Configurações de Admin" primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
                        </ListItem>
                    )}

                    <ListItem
                        component="a"
                        href="https://wa.me/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            borderRadius: 1.5,
                            py: 0.7,
                            px: 1.5,
                            cursor: 'pointer',
                            color: 'text.secondary',
                            textDecoration: 'none',
                            '&:hover': { bgcolor: 'action.hover', color: 'text.primary' }
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5, color: 'inherit' }}>
                            <WhatsAppIcon sx={{ fontSize: 18, color: '#25D366' }} />
                        </ListItemIcon>
                        <ListItemText primary="Ajuda" primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
                    </ListItem>
                </List>
            </Box>
        </Drawer>
    );
}
